import {
    initImagePreviews,
    initLazyImages,
} from "../img-preview";

const POLLED_UPLOAD_STATUSES = new Set([
    "uploading",
    "scanning",
    "processing",
]);

export function buildUploadStatusUrl(statusUrl, fileIds = []) {
    const url = new URL(statusUrl, window.location.origin);

    url.searchParams.delete("file_ids");
    url.searchParams.delete("file_ids[]");

    fileIds.forEach((fileId) => {
        url.searchParams.append("file_ids[]", String(fileId));
    });

    return url.toString();
}

export async function fetchUploadStatuses(statusUrl, fileIds = []) {
    const response = await fetch(buildUploadStatusUrl(statusUrl, fileIds), {
        headers: {
            Accept: "application/json",
        },
    });

    const body = await response.json().catch(() => ({}));

    if (!response.ok) {
        throw new Error(
            body?.message || `Request failed with status ${response.status}`,
        );
    }

    return Array.isArray(body?.data) ? body.data : [];
}

function createFileStatusMonitorData() {
    return {
        statusUrl: null,
        pollIntervalMs: 2500,
        reloadOnChange: false,
        timerId: null,
        active: false,
        init() {
            this.statusUrl = this.statusUrl || this.$el.dataset.uploadStatusUrl;
            this.reloadOnChange =
                this.$el.dataset.uploadStatusReloadOnChange === "true";

            const interval = Number.parseInt(
                String(this.$el.dataset.uploadStatusPollInterval || this.pollIntervalMs),
                10,
            );

            this.pollIntervalMs = Number.isFinite(interval) ? interval : 2500;
            initLazyImages(this.$el);
            initImagePreviews(this.$el);
            this.start();
        },
        pendingFileIds() {
            return [...this.$el.querySelectorAll("[data-upload-file-id][data-upload-file-status]")]
                .map((element) => ({
                    fileId: Number.parseInt(
                        String(element.dataset.uploadFileId || "0"),
                        10,
                    ),
                    status: String(element.dataset.uploadFileStatus || ""),
                }))
                .filter(
                    ({ fileId, status }) =>
                        fileId > 0 && POLLED_UPLOAD_STATUSES.has(status),
                )
                .map(({ fileId }) => fileId)
                .filter((fileId, index, fileIds) => fileIds.indexOf(fileId) === index);
        },
        start() {
            if (this.active || !this.statusUrl) {
                return;
            }

            this.active = true;
            this.queueNextTick();
        },
        stop() {
            this.active = false;

            if (this.timerId) {
                window.clearTimeout(this.timerId);
                this.timerId = null;
            }
        },
        queueNextTick(delay = this.pollIntervalMs) {
            this.stop();
            this.active = true;
            this.timerId = window.setTimeout(() => this.tick(), delay);
        },
        async tick() {
            if (!this.active || !this.statusUrl) {
                return;
            }

            const fileIds = this.pendingFileIds();

            if (fileIds.length === 0) {
                this.stop();
                return;
            }

            if (document.visibilityState === "hidden") {
                this.queueNextTick(Math.max(this.pollIntervalMs * 2, 5000));
                return;
            }

            let statusPayloads = [];

            try {
                statusPayloads = await fetchUploadStatuses(this.statusUrl, fileIds);
            } catch (_error) {
                this.queueNextTick(Math.max(this.pollIntervalMs * 2, 5000));
                return;
            }

            const hasStatusChange = statusPayloads.some((statusPayload) => {
                const fileId = Number.parseInt(
                    String(statusPayload?.file_id || "0"),
                    10,
                );

                if (fileId <= 0) {
                    return false;
                }

                const nextStatus = String(statusPayload?.status || "");
                const nextScanStatus = String(statusPayload?.scan_status || "");

                return [...this.$el.querySelectorAll(`[data-upload-file-id="${fileId}"]`)]
                    .some((element) =>
                        element.dataset.uploadFileStatus !== nextStatus ||
                        String(element.dataset.uploadFileScanStatus || "") !== nextScanStatus,
                    );
            });

            if (hasStatusChange && this.reloadOnChange) {
                window.location.reload();
                return;
            }

            const hasPendingFiles = this.pendingFileIds().length > 0;

            if (!hasPendingFiles) {
                this.stop();
                return;
            }

            this.queueNextTick();
        },
    };
}

export function registerFileStatusMonitor(Alpine) {
    Alpine.data("fileStatusMonitor", createFileStatusMonitorData);
}
